import React, { useState, useEffect } from "react";
import {
  Drawer,
  Table,
  Typography,
  Divider,
  Tag,
  Space,
  InputNumber,
  message,
  Tooltip,
  Button,
} from "antd";
import { CloseOutlined, FileDoneOutlined } from "@ant-design/icons";
import { getStockOrdenesServicios } from "../../services/entregas/servicesOrdenesCompra";
import ActaEntregaPDF from "./FormatoAcataEntragaSostenibilidad";

const { Title } = Typography;

const RealizarEntregaPaati = ({ open, onClose, record }) => {
  const [stockData, setStockData] = React.useState([]);
  const [bodegaFilters, setBodegaFilters] = React.useState([]);
  const [proveedorFilters, setProveedorFilters] = React.useState([]);
  const [productosEntrega, setProductosEntrega] = useState([]);
  const [mostrarPDF, setMostrarPDF] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const result = await getStockOrdenesServicios();
      if (result.status === 200) {
        const data = result.data;

        setStockData(data);

        // Filtros dinámicos
        const bodegas = [...new Set(data.map((d) => d.nombre_bodega))].map(
          (b) => ({ text: b, value: b })
        );
        const proveedores = [
          ...new Set(data.map((d) => d.proveedor).filter(Boolean)),
        ].map((p) => ({ text: p, value: p }));

        setBodegaFilters(bodegas);
        setProveedorFilters(proveedores);
      }
    };
    fetchData();
  }, []);

  // Extraer y consolidar todos los productos
  // Actualiza productos con cantidadEntregar (usamos setProductosEntrega)
  useEffect(() => {
    if (!record || !record.dataItems) return;
    const productosConCantidad = record.dataItems.flatMap((bloque) =>
      bloque.items.map((item) => {
        const stockEntry = stockData.find(
          (stock) =>
            stock.producto_id === item.oidProducto &&
            stock.bodega_oidMunicipio === record.oidMunicipio
        );

        return {
          ...item,
          categoria: bloque.title,
          enbodega: stockEntry ? stockEntry.stock_total : 0,
          cantidadEntregar: 0,
        };
      })
    );

    setProductosEntrega(productosConCantidad);
  }, [record, stockData]);

  const handleCantidadEntregarChange = (value, index) => {
    const updated = [...productosEntrega];
    const cantidadAsignada = updated[index].cantidad;

    if (value > cantidadAsignada) {
      message.warning(
        "La cantidad a entregar no puede superar la cantidad asignada."
      );
      return;
    }
    if (value > cantidadAsignada || value > updated[index].enbodega) {
      message.warning(
        "La cantidad a entregar no puede superar la asignada ni el stock disponible."
      );
      return;
    }

    updated[index].cantidadEntregar = value;
    setProductosEntrega(updated);
  };

  const columns = [
    { title: "Código", dataIndex: "codigo", key: "codigo", width: 100 },
    {
      title: "Nombre del producto",
      dataIndex: "nombre_producto",
      key: "nombre_producto",
      ellipsis: true,
      width: 500,
      render: (text) => (
        <Tooltip title={text}>
          <span
            style={{
              display: "inline-block",
              maxWidth: 450,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {text}
          </span>
        </Tooltip>
      ),
    },
    { title: "Unidad", dataIndex: "unidad", key: "unidad" },
    {
      title: "(C) asignada)",
      dataIndex: "cantidad",
      key: "cantidad",
      align: "center",
    },
    {
      title: "(C) En bodega",
      dataIndex: "enbodega",
      key: "enbodega",
      align: "center",
    },
    {
      title: "(C) a entregar",
      dataIndex: "cantidadEntregar",
      key: "cantidadEntregar",
      align: "center",
      render: (_, record, index) => (
        <InputNumber
          min={0}
          max={record.cantidad}
          value={record.cantidadEntregar}
          disabled={record.enbodega === 0}
          onChange={(value) => handleCantidadEntregarChange(value, index)}
          style={{ width: 100, textAlign: "center" }}
        />
      ),
    },

    {
      title: "Categoría",
      dataIndex: "categoria",
      key: "categoria",
      render: (text) => <span>{text || "N/A"}</span>,
    },
  ];

  // Función para entregar
  const handleEntrega = () => {
    const entregasValidas = productosEntrega.filter(
      (p) => p.cantidadEntregar > 0
    );

    if (entregasValidas.length === 0) {
      message.warning("No hay productos con cantidades válidas para entregar.");
      return;
    }

    // Aquí puedes hacer un POST al backend para registrar la entrega
    message.success("Entrega registrada (simulada).");
    setMostrarPDF(true); // ✅ Mostrar el PDF
  };

  return (
    <Drawer
      title={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            position: "relative",
          }}
        >
          <h6 style={{ margin: 0, fontWeight: "bold", paddingRight: 50 }}>
            Realizar entrega - Código ${record?.codSir}
          </h6>
          <Button
            icon={<CloseOutlined />}
            onClick={() => onClose()}
            style={{
              position: "absolute",
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
              border: "none",
              boxShadow: "none",
            }}
          />
        </div>
      }
      placement="right"
      width="100vw"
      style={{ height: "100vh", padding: 0 }}
      onClose={onClose}
      open={open}
    >
      <Title level={5}>Datos del beneficiario</Title>

      <Space wrap size={[8, 16]}>
        <Tag color="warning">
          <strong>Código:</strong> {record?.codSir}
        </Tag>
        <Tag color="blue">
          <strong>Nombre:</strong> {record?.nombres_if}
        </Tag>
        <Tag color="green">
          <strong>Departamento:</strong> {record?.departamento}
        </Tag>
        <Tag color="volcano">
          <strong>Municipio:</strong> {record?.municipio}
        </Tag>
      </Space>
      <Divider />

      {mostrarPDF ? (
        <div style={{ height: "calc(100vh - 200px)", overflow: "auto" }}>
          <ActaEntregaPDF record={record} productos={productosEntrega} />
        </div>
      ) : (
        <>
          <Space
            style={{
              marginBottom: 16,
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Title level={5}>Productos a entregar</Title>
            <Button
              type="primary"
              onClick={handleEntrega}
              style={{ marginTop: 16 }}
              icon={<FileDoneOutlined />}
            >
              Realizar entrega
            </Button>
          </Space>
          <Table
            dataSource={productosEntrega}
            columns={columns}
            rowKey={(row) => `${row.oidProducto}-${row.codigo}`}
            pagination={false}
            scroll={{ x: "max-content" }}
            style={{ marginBottom: 20 }}
            size="small"
          />
        </>
      )}
    </Drawer>
  );
};

export default RealizarEntregaPaati;
